import {useCallback} from 'react';

import type {
    OnShippingAddressChangeActions,
    OnShippingAddressChangeData,
} from '@paypal/paypal-js/types';

import {handlePayPalDesktopErrorAnalytic} from 'features/expressPayments/payPalV3/utils/analytics';
import {logWithSentry} from 'libs/sentry/sentry';
import {ShippingMethodType} from 'types/shippingMethodsType';

import {OnShippingAddressChangeAdditionalType} from './types';
import {useGetAndUpdateTotals} from './useGetAndUpdateTotals';
import {useSaveMockAddress} from './useSaveMockAddress';

/**
 * Handle paypal shipping address change
 */
export const useShippingAddressChange = () => {
    const saveAddressAndShipping = useSaveMockAddress();

    const getAndUpdateTotals = useGetAndUpdateTotals();
    const handleError = useCallback(
        async (error: Record<string, unknown>, actions: OnShippingAddressChangeActions) => {
            await actions.reject();
            handlePayPalDesktopErrorAnalytic(error);
            logWithSentry(error as unknown as Error, 'PayPal V3 change address error');
        },
        [],
    );
    const onShippingAddressChange = useCallback(
        async (
            {shippingAddress}: OnShippingAddressChangeData,
            actions: OnShippingAddressChangeActions,
            additionalInfo: OnShippingAddressChangeAdditionalType,
        ): Promise<ShippingMethodType[]> => {
            try {
                if (!shippingAddress) {
                    logWithSentry(new Error('Missing shipping address from paypal v3!'));
                    return [];
                }
                const availableShippings = await saveAddressAndShipping(
                    shippingAddress,
                    additionalInfo,
                );

                await getAndUpdateTotals(additionalInfo.id, availableShippings);
                return availableShippings;
            } catch (error) {
                handleError(error, actions);
                return [];
            }
        },
        [getAndUpdateTotals, handleError, saveAddressAndShipping],
    );

    return onShippingAddressChange;
};
